import React from 'react';
import { Link } from 'react-router-dom';
import { LandingNav } from '../components/LandingNav';
import { LandingFooter } from '../components/LandingFooter';
import './privacyPolicy.css';

export const PrivacyPolicy: React.FC = () => {
    const lastUpdated = new Date().toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    return (
        <div className="raabta-landing">
            <LandingNav showMenu={false} />

            <main style={{ padding: '2rem 4rem' }}>
                <header className="pp-header">
                    <h1 className="pp-title">Privacy Policy</h1>
                    <p className="pp-subtitle">
                        Your trust is fundamental to us. Here is how we collect, use, store, and protect your data on the Raabta platform.
                    </p>
                    <div className="pp-meta">
                        Last updated: {lastUpdated} · Version 1.0
                    </div>
                </header>

                <article className="pp-article">
                    <section className="pp-sec">
                        <h2>1. Introduction</h2>
                        <p>
                            Raabta ("we", "our", "us") provides a school communication and ticketing platform
                            that helps schools, parents, employees, and administrators connect, raise queries,
                            and resolve issues efficiently.
                        </p>
                        <p>
                            This Privacy Policy explains how we collect, use, store, and protect personal
                            information when you use the Raabta web application and related services. By using
                            Raabta, you agree to the practices described below.
                        </p>
                    </section>

                    <section className="pp-sec">
                        <h2>2. Data We Collect</h2>
                        <p>We collect only the information necessary to operate the platform:</p>
                        <ul>
                            <li><strong>Account Information</strong>: Name, email, role, school assignment, and optional profile photos.</li>
                            <li><strong>School Data</strong>: Branding assets, departments, workflows, tags, and knowledge-base documents.</li>
                            <li><strong>Tickets & Communications</strong>: Subjects, descriptions, status updates, comments, attachments, and audit history.</li>
                            <li><strong>Student & Parent Data</strong>: Names, classes, and parent-child relationships (provided by school administrators).</li>
                            <li><strong>Usage Data</strong>: Login timestamps, page views, system actions, and browser/device metadata.</li>
                            <li><strong>Cookies</strong>: Authentication tokens, theme preferences, and session state.</li>
                        </ul>
                    </section>

                    <section className="pp-sec">
                        <h2>3. How We Use Your Data</h2>
                        <p>Your data is used to power the core services of our platform:</p>
                        <ul>
                            <li>To authenticate your identity and provide role-based access to features.</li>
                            <li>To route tickets to the correct department and personnel automatically.</li>
                            <li>To generate dashboards, performance statistics, and SLA insights for your school.</li>
                            <li>To power the AI knowledge assistant using school-uploaded documents.</li>
                            <li>To send notifications about assignments, escalations, and replies.</li>
                            <li>To improve the platform through aggregated, anonymized usage analytics.</li>
                            <li>To comply with legal and regulatory obligations.</li>
                        </ul>
                    </section>

                    <section className="pp-sec">
                        <h2>4. Data Sharing</h2>
                        <p>
                            Raabta operates as a multi-tenant platform. Data within a school is strictly isolated and is only visible
                            to members of that school based on their roles and permissions.
                        </p>
                        <p className="pp-emphasis">
                            <strong>We never sell your personal data.</strong>
                        </p>
                        <p>We share information only in the following scenarios:</p>
                        <ul>
                            <li>With trusted infrastructure providers (cloud hosting, database management, email delivery services) under strict data-processing agreements.</li>
                            <li>When required by law, subpoena, court order, or to protect the safety and rights of our users.</li>
                            <li>With the explicit consent of authorized school administrators.</li>
                        </ul>
                    </section>

                    <section className="pp-sec">
                        <h2>5. Data Storage & Security</h2>
                        <p>
                            All data is stored on secure cloud infrastructure with industry-standard encryption in transit (HTTPS/TLS)
                            and at rest. Access is strictly restricted via role-based access controls and monitored audit logs.
                        </p>
                        <p>Key security practices include:</p>
                        <ul>
                            <li>Full encryption at rest and in transit.</li>
                            <li>Least-privilege role-based access control.</li>
                            <li>Regular automated system backups.</li>
                            <li>Continuous security monitoring and logging.</li>
                        </ul>
                    </section>

                    <section className="pp-sec">
                        <h2>6. Data Retention</h2>
                        <p>
                            We retain personal data only as long as necessary to provide our service or as required by law.
                            When a school account is deactivated or you request account deletion, we remove or anonymize
                            associated personal data within <strong>30 days</strong>, subject to legal and operational requirements.
                        </p>
                    </section>

                    <section className="pp-sec">
                        <h2>7. Your Rights</h2>
                        <p>Depending on your jurisdiction, you may have the right to:</p>
                        <ul>
                            <li>Access the personal data we hold about you.</li>
                            <li>Request correction of inaccurate or incomplete data.</li>
                            <li>Request deletion of your data (see our <Link to="/delete-account">Delete Account</Link> page).</li>
                            <li>Object to or restrict certain processing activities.</li>
                            <li>Request a copy of your data in a portable format.</li>
                        </ul>
                        <p>
                            To exercise these rights, please contact your school administrator or reach out to us directly.
                        </p>
                    </section>

                    <section className="pp-sec">
                        <h2>8. Children's Privacy</h2>
                        <p>
                            Raabta is designed for school staff and parents. Student records (such as a child's name and class)
                            are managed by authorized school administrators on behalf of the school, in compliance with applicable child-protection laws.
                        </p>
                        <p>
                            Children do not directly create accounts on the platform. If you believe a child's data has been
                            improperly added without authorization, please contact us immediately.
                        </p>
                    </section>

                    <section className="pp-sec">
                        <h2>9. Cookies & Tracking</h2>
                        <p>
                            We use essential cookies for authentication and session management, and minimal analytics cookies
                            to understand product usage. You can disable cookies in your browser, but core features (such as login)
                            will not function without them.
                        </p>
                    </section>

                    <section className="pp-sec">
                        <h2>10. Changes to This Policy</h2>
                        <p>
                            We may update this Privacy Policy from time to time. Material changes will be communicated
                            through the application interface or via email. The "Last updated" date at the top of this
                            page indicates when the policy was last revised.
                        </p>
                    </section>

                    <section className="pp-sec">
                        <h2>11. Contact Us</h2>
                        <p>
                            If you have questions about this Privacy Policy or how your data is handled, you can email our privacy team at:
                        </p>
                        <p className="pp-contact">
                            <strong>privacy@raabta.app</strong>
                            <br />
                            <span className="pp-contact-sub">We aim to respond within 2 business days.</span>
                        </p>
                    </section>
                </article>
            </main>

            <LandingFooter />
        </div>
    );
};
